import { createContext, useContext, useState, useEffect } from "react";

const TRANSLATIONS = {
  en: {
    // Nav & common
    "nav.login": "Log in",
    "nav.signup": "Get started",
    "nav.dashboard": "Go to Dashboard",
    "nav.back_home": "Back home",
    "nav.logout": "Log out",
    "side.syllabus": "Syllabus",
    "side.profile": "Profile",
    "side.signed_in_as": "Signed in as",
    "side.lesson_planner": "Lesson planner",
    "common.loading": "Loading…",
    "common.save": "Save",
    "common.saving": "Saving…",
    "common.back": "Back",
    "common.continue": "Continue",
    "common.cancel": "Cancel",
    "common.download": "Download",
    "common.share": "Share",
    "common.copied": "Link copied",
    // Landing
    "landing.badge": "For English teachers in France · CM2 / 6ème",
    "landing.hero_title_a": "Plan a whole",
    "landing.hero_title_b": "school year of",
    "landing.hero_title_c": "English lessons",
    "landing.hero_title_d": "in 30 seconds.",
    "landing.hero_desc": "THE TEACHKIT builds your entire 32-week syllabus, aligned with the French national curriculum for ages 10-12. Every lesson follows the TEFL PPP framework with objectives, grammar, vocabulary and materials — ready to teach.",
    "landing.cta_primary": "Build my syllabus",
    "landing.cta_secondary": "See how it works",
    "landing.features_kicker": "What you get",
    "landing.features_title": "A dashboard that respects your time.",
    "landing.footer_title": "Ready to reclaim your Sunday nights?",
    "landing.footer_sub": "Join teachers who plan smarter, not harder.",
    "landing.footer_cta": "Get started free",
    // Auth
    "auth.signup_kicker": "Create your account",
    "auth.login_kicker": "Welcome back",
    "auth.signup_title": "Start planning smarter.",
    "auth.login_title": "Log in to your kit.",
    "auth.name": "Full name",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.create_account": "Create account",
    "auth.login": "Log in",
    "auth.have_account": "Already have an account?",
    "auth.new_here": "New here?",
    "auth.welcome": "Welcome to THE TEACHKIT!",
    "auth.welcome_back": "Welcome back!",
    "auth.side_a": "Your English",
    "auth.side_b": "lesson planning",
    "auth.side_c": "co-pilot.",
    "auth.side_sub": "32 weeks. PPP. French curriculum. Ready in one click.",
    "auth.side_footer": "Built for teachers in France",
    // Dashboard
    "dash.kicker": "Your syllabus · 32 weeks",
    "dash.greet_morning": "Good morning",
    "dash.greet_afternoon": "Good afternoon",
    "dash.greet_evening": "Good evening",
    "dash.you_are_week": "You are in",
    "dash.of_school_year": "of the school year.",
    "dash.on_holiday_a": "School is on",
    "dash.on_holiday_b": "holiday. Next:",
    "dash.week": "Week",
    "dash.open_week": "Open this week",
    "dash.export_syllabus": "Export syllabus PDF",
    "dash.regenerate": "Regenerate",
    "dash.regenerating": "Regenerating…",
    "dash.no_syllabus_kicker": "No syllabus yet",
    "dash.no_syllabus_title": "Let's build your year.",
    "dash.no_syllabus_desc": "Generate a 32-week English syllabus tailored to your class in seconds.",
    "dash.generate_btn": "Generate 32-week syllabus",
    "dash.batch_kicker": "Batch pack generation",
    "dash.batch_progress": "weeks fully prepared",
    "dash.batch_working": "Working on week",
    "dash.batch_done": "done",
    "dash.batch_hint": "Feel free to close this tab — packs are generated on the server.",
    "dash.batch_generate": "Generate remaining packs",
    "dash.batch_all_ready": "All 32 packs ready",
    "dash.holidays_kicker": "French school holidays",
    "dash.holidays_zone": "Zone",
    "dash.holidays_none": "not set",
    "dash.holidays_hint": "Pick your zone in Profile so vacations skip automatically in the current-week calendar.",
    "dash.share_btn": "Share syllabus",
    "dash.past": "Past",
    "dash.current_week": "Current week",
    "dash.grammar": "Grammar",
    // Lesson detail
    "lesson.back": "Back to syllabus",
    "lesson.quick_pdf": "Quick PDF",
    "lesson.edit": "Edit",
    "lesson.grammar_focus": "Grammar focus",
    "lesson.vocabulary": "Vocabulary",
    "lesson.objectives": "Objectives",
    "lesson.materials": "Materials",
    "lesson.teacher_notes": "Teacher notes",
    "lesson.notes_placeholder": "Add your notes for this week…",
    "lesson.pack_kicker": "Full teacher pack",
    "lesson.pack_title_generated": "Print-ready pack",
    "lesson.pack_title_missing": "Generate the full teacher pack",
    "lesson.pack_desc": "Timed activities with teacher scripts, student activity sheets (gap fills, T/F, MCQs, role-plays), and a matching answer key — all print-ready. Powered by Claude Sonnet 5.",
    "lesson.pack_generate": "Generate teacher pack",
    "lesson.pack_generating": "Generating (10-25s)…",
    "lesson.pack_regenerate": "Regenerate",
    "lesson.pack_doc": "Document",
    "lesson.pack_teacher": "Teacher plan",
    "lesson.pack_student": "Student sheets",
    "lesson.pack_answers": "Answer key",
    "lesson.pack_preview": "Pack preview",
    "lesson.aim": "Aim",
    "lesson.homework_kicker": "Homework",
    "lesson.homework_title_missing": "Generate a matching homework sheet",
    "lesson.homework_title_generated": "Homework ready",
    "lesson.homework_desc": "One printable A4 page reinforcing this week's grammar and vocabulary — with an answer key for you.",
    "lesson.homework_generate": "Generate homework",
    "lesson.homework_generating": "Generating…",
    "lesson.homework_download": "Download homework",
    "lesson.homework_download_answers": "Answer key",
    "lesson.swap_title": "Swap activity",
    "lesson.swap_ai": "Suggest with AI",
    "lesson.swap_ai_busy": "Asking Claude…",
    "lesson.swap_save": "Save swap",
    // Profile
    "profile.kicker": "Your account",
    "profile.title": "Profile & preferences",
    "profile.section_teacher": "Teacher & school",
    "profile.full_name": "Full name",
    "profile.email": "Email",
    "profile.school_name": "School name",
    "profile.city": "City",
    "profile.class_type": "Class type",
    "profile.class_level": "Level",
    "profile.year_start": "School year start",
    "profile.zone": "French holiday zone",
    "profile.zone_none": "— None (skip holidays)",
    "profile.priorities_section": "Priority skills (drag to reorder)",
    "profile.activities_section": "Preferred activity types",
    "profile.save": "Save changes",
    // Onboarding
    "onb.step_of": "Step {n} of 3",
    "onb.step1_title": "Tell us about your school.",
    "onb.step1_desc": "We'll brand your PDFs with these details.",
    "onb.step2_title": "Rank your priority skills.",
    "onb.step2_desc": "Drag to reorder. The generator will emphasise your top skill in Production activities.",
    "onb.step3_title": "Pick your favourite activity types.",
    "onb.step3_desc": "We'll weave these across the 32-week plan.",
    "onb.finish": "Generate my syllabus",
    "onb.finish_busy": "Generating your syllabus…",
    // Share
    "share.title": "Shared syllabus",
    "share.by": "Shared by",
    "share.readonly": "Read-only view",
    "share.on_title": "Public link is on",
    "share.off_title": "Share with colleagues",
    "share.card_desc": "A read-only view your colleagues can open without an account.",
    "share.enable_link": "Enable link",
    "share.copy_link": "Copy link",
    "share.disabled": "Sharing disabled",
    // Gallery
    "gallery.subtitle": "Public gallery",
    "gallery.kicker": "Peek inside real classrooms",
    "gallery.title": "See how other teachers plan their year.",
    "gallery.desc": "Real syllabi shared by real teachers in France. Get inspired, borrow ideas, and build your own.",
    "gallery.empty_title": "No public syllabi yet.",
    "gallery.empty_desc": "Be the first! Enable sharing from your dashboard to inspire your peers.",
    "gallery.card_default_title": "English syllabus",
    "gallery.view": "View syllabus",
    "gallery.link": "Public gallery",
    // Analytics
    "stats.kicker": "Your progress",
    "stats.title": "Year at a glance",
    "stats.taught": "Weeks taught",
    "stats.packed": "Packs ready",
    "stats.homework": "With homework",
    "stats.streak": "Weeks left this year",
    // Homework send
    "hw.send_families": "Send to families",
    "hw.send_no_recipients": "Add family emails in your Profile first.",
    "hw.send_email_subject": "English homework — Week {n}: {title}",
    "hw.send_email_body_1": "Hello families,",
    "hw.send_email_body_2": "Please find attached this week's English homework: {title} (about {min} minutes).",
    "hw.send_email_body_3": "The PDF was downloaded to your computer — please attach it to this email before sending.",
    "hw.send_email_body_4": "Best regards,",
    // Profile extras
    "profile.family_emails": "Family emails (one per line)",
    "profile.family_emails_hint": "Used to draft homework emails in one click.",
    "profile.gallery_optin": "List in the public gallery",
    "hw.generated": "Homework generated",
    "hw.download_ok": "Homework downloaded",
    "hw.answers_ok": "Answer key downloaded",
    "hw.failed": "Homework generation failed",
    "hw.draft_opened": "Draft opened for {n} family recipients",
    "pack.generated": "Teacher pack generated — download PDFs below",
    "pack.failed": "Pack generation failed",
    "pack.teacher_ok": "Teacher plan downloaded",
    "pack.student_ok": "Student sheets downloaded",
    "pack.answers_ok": "Answer key downloaded",
    "pdf.downloaded": "PDF downloaded",
    "lesson.saved": "Lesson saved",
    "lesson.save_failed": "Failed to save",
    "lesson.swap_ok": "Activity swapped",
    "lesson.swap_failed": "Swap failed",
    "lesson.ai_ok": "AI suggested a new activity",
    "lesson.ai_failed": "AI generation failed",
    // Chat
    "chat.fab": "Ask the AI",
    "chat.title": "Lesson coach",
    "chat.intro_a": "I'm your TEFL coach for",
    "chat.intro_b": "Ask me to rephrase, level a task, or suggest a warmer.",
    "chat.placeholder": "Ask about this lesson…",
    "chat.error": "Chat failed — please retry.",
    "chat.model_footer": "Powered by Claude Sonnet 5",
    "chat.suggest_rephrase": "Rephrase the Production activity in simpler English.",
    "chat.suggest_level_down": "Give me an easier version of this lesson for a shy group.",
    "chat.suggest_warmer": "Suggest a 5-minute warmer that fits this theme.",
    "chat.suggest_example": "Give 3 concrete example sentences using today's grammar.",
    // Newsletter
    "news.card_kicker": "Sunday newsletter",
    "news.card_title": "Draft this week's family email",
    "news.card_desc": "One-click friendly email summarising Week {n}: theme, vocab and any homework — auto-addressed to your family list.",
    "news.card_btn": "Draft newsletter",
    "news.no_recipients": "Add family emails in your Profile to send newsletters.",
    "news.subject": "This week in English — Week {n}: {title}",
    "news.body_hello": "Hello families,",
    "news.body_intro": "Here's what your child will explore in English this week:",
    "news.body_theme": "Theme:",
    "news.body_grammar": "Grammar focus:",
    "news.body_vocab": "Key vocabulary:",
    "news.body_homework": "Homework:",
    "news.body_homework_yes": "A short worksheet ({min} min). Ask your child to show it to you.",
    "news.body_homework_no": "No homework this week — just curiosity and enthusiasm!",
    "news.body_share": "Full syllabus:",
    "news.body_close": "Have a great week,",
    "news.draft_opened": "Newsletter draft opened",
  },
  fr: {
    // Nav & common
    "nav.login": "Connexion",
    "nav.signup": "Commencer",
    "nav.dashboard": "Tableau de bord",
    "nav.back_home": "Retour",
    "nav.logout": "Déconnexion",
    "side.syllabus": "Programme",
    "side.profile": "Profil",
    "side.signed_in_as": "Connecté en tant que",
    "side.lesson_planner": "Préparation des cours",
    "common.loading": "Chargement…",
    "common.save": "Enregistrer",
    "common.saving": "Enregistrement…",
    "common.back": "Retour",
    "common.continue": "Continuer",
    "common.cancel": "Annuler",
    "common.download": "Télécharger",
    "common.share": "Partager",
    "common.copied": "Lien copié",
    "landing.badge": "Pour les enseignants d'anglais en France · CM2 / 6ème",
    "landing.hero_title_a": "Préparez une",
    "landing.hero_title_b": "année entière",
    "landing.hero_title_c": "de cours d'anglais",
    "landing.hero_title_d": "en 30 secondes.",
    "landing.hero_desc": "THE TEACHKIT génère votre programme complet de 32 semaines, aligné sur le programme officiel de l'Éducation nationale pour les 10-12 ans. Chaque cours suit le cadre TEFL PPP avec objectifs, grammaire, vocabulaire et matériel — prêt à enseigner.",
    "landing.cta_primary": "Créer mon programme",
    "landing.cta_secondary": "Voir comment ça marche",
    "landing.features_kicker": "Ce que vous obtenez",
    "landing.features_title": "Un tableau de bord qui respecte votre temps.",
    "landing.footer_title": "Envie de récupérer vos dimanches soirs ?",
    "landing.footer_sub": "Rejoignez les enseignants qui planifient plus intelligemment.",
    "landing.footer_cta": "Commencer gratuitement",
    "auth.signup_kicker": "Créez votre compte",
    "auth.login_kicker": "Bon retour",
    "auth.signup_title": "Planifiez plus intelligemment.",
    "auth.login_title": "Accédez à votre kit.",
    "auth.name": "Nom complet",
    "auth.email": "Email",
    "auth.password": "Mot de passe",
    "auth.create_account": "Créer un compte",
    "auth.login": "Se connecter",
    "auth.have_account": "Vous avez déjà un compte ?",
    "auth.new_here": "Nouveau ?",
    "auth.welcome": "Bienvenue sur THE TEACHKIT !",
    "auth.welcome_back": "Bon retour !",
    "auth.side_a": "Votre copilote",
    "auth.side_b": "pour préparer",
    "auth.side_c": "vos cours d'anglais.",
    "auth.side_sub": "32 semaines. PPP. Programme officiel. Prêt en un clic.",
    "auth.side_footer": "Conçu pour les enseignants en France",
    "dash.kicker": "Votre programme · 32 semaines",
    "dash.greet_morning": "Bonjour",
    "dash.greet_afternoon": "Bon après-midi",
    "dash.greet_evening": "Bonsoir",
    "dash.you_are_week": "Vous êtes en",
    "dash.of_school_year": "de l'année scolaire.",
    "dash.on_holiday_a": "L'école est en vacances",
    "dash.on_holiday_b": ". Prochaine :",
    "dash.week": "Semaine",
    "dash.open_week": "Ouvrir cette semaine",
    "dash.export_syllabus": "Exporter le programme PDF",
    "dash.regenerate": "Régénérer",
    "dash.regenerating": "Régénération…",
    "dash.no_syllabus_kicker": "Aucun programme",
    "dash.no_syllabus_title": "Construisons votre année.",
    "dash.no_syllabus_desc": "Générez un programme d'anglais sur 32 semaines adapté à votre classe en quelques secondes.",
    "dash.generate_btn": "Générer le programme de 32 semaines",
    "dash.batch_kicker": "Génération groupée",
    "dash.batch_progress": "semaines prêtes",
    "dash.batch_working": "Semaine en cours",
    "dash.batch_done": "faits",
    "dash.batch_hint": "Vous pouvez fermer cet onglet — la génération se fait sur le serveur.",
    "dash.batch_generate": "Générer les kits restants",
    "dash.batch_all_ready": "Les 32 kits sont prêts",
    "dash.holidays_kicker": "Vacances scolaires",
    "dash.holidays_zone": "Zone",
    "dash.holidays_none": "non définie",
    "dash.holidays_hint": "Choisissez votre zone dans le Profil pour ignorer automatiquement les vacances.",
    "dash.share_btn": "Partager le programme",
    "dash.past": "Passée",
    "dash.current_week": "Semaine actuelle",
    "dash.grammar": "Grammaire",
    "lesson.back": "Retour au programme",
    "lesson.quick_pdf": "PDF rapide",
    "lesson.edit": "Modifier",
    "lesson.grammar_focus": "Point de grammaire",
    "lesson.vocabulary": "Vocabulaire",
    "lesson.objectives": "Objectifs",
    "lesson.materials": "Matériel",
    "lesson.teacher_notes": "Notes de l'enseignant",
    "lesson.notes_placeholder": "Ajoutez vos notes pour cette semaine…",
    "lesson.pack_kicker": "Kit complet enseignant",
    "lesson.pack_title_generated": "Kit prêt à imprimer",
    "lesson.pack_title_missing": "Générer le kit enseignant complet",
    "lesson.pack_desc": "Activités chronométrées avec scripts enseignant, feuilles d'activités élèves (textes à trous, V/F, QCM, jeux de rôle) et corrigé — tout prêt à imprimer. Propulsé par Claude Sonnet 5.",
    "lesson.pack_generate": "Générer le kit",
    "lesson.pack_generating": "Génération (10-25s)…",
    "lesson.pack_regenerate": "Régénérer",
    "lesson.pack_doc": "Document",
    "lesson.pack_teacher": "Plan enseignant",
    "lesson.pack_student": "Fiches élèves",
    "lesson.pack_answers": "Corrigé",
    "lesson.pack_preview": "Aperçu du kit",
    "lesson.aim": "Objectif",
    "lesson.homework_kicker": "Devoirs",
    "lesson.homework_title_missing": "Générer une feuille de devoirs",
    "lesson.homework_title_generated": "Devoirs prêts",
    "lesson.homework_desc": "Une page A4 imprimable qui renforce la grammaire et le vocabulaire de la semaine — avec le corrigé pour vous.",
    "lesson.homework_generate": "Générer les devoirs",
    "lesson.homework_generating": "Génération…",
    "lesson.homework_download": "Télécharger les devoirs",
    "lesson.homework_download_answers": "Corrigé",
    "lesson.swap_title": "Remplacer l'activité",
    "lesson.swap_ai": "Suggérer avec l'IA",
    "lesson.swap_ai_busy": "Consultation de Claude…",
    "lesson.swap_save": "Enregistrer",
    "profile.kicker": "Votre compte",
    "profile.title": "Profil et préférences",
    "profile.section_teacher": "Enseignant et école",
    "profile.full_name": "Nom complet",
    "profile.email": "Email",
    "profile.school_name": "Nom de l'école",
    "profile.city": "Ville",
    "profile.class_type": "Type de classe",
    "profile.class_level": "Niveau",
    "profile.year_start": "Début de l'année scolaire",
    "profile.zone": "Zone de vacances",
    "profile.zone_none": "— Aucune (ignorer les vacances)",
    "profile.priorities_section": "Compétences prioritaires (glisser pour réordonner)",
    "profile.activities_section": "Types d'activités préférés",
    "profile.save": "Enregistrer",
    "onb.step_of": "Étape {n} sur 3",
    "onb.step1_title": "Parlez-nous de votre école.",
    "onb.step1_desc": "Nous personnaliserons vos PDF avec ces détails.",
    "onb.step2_title": "Classez vos compétences prioritaires.",
    "onb.step2_desc": "Glissez pour réordonner. Le générateur mettra l'accent sur votre compétence prioritaire lors des activités de Production.",
    "onb.step3_title": "Choisissez vos types d'activités préférés.",
    "onb.step3_desc": "Nous les intégrerons dans les 32 semaines.",
    "onb.finish": "Générer mon programme",
    "onb.finish_busy": "Génération en cours…",
    "share.title": "Programme partagé",
    "share.by": "Partagé par",
    "share.readonly": "Vue en lecture seule",
    "share.on_title": "Lien public activé",
    "share.off_title": "Partager avec vos collègues",
    "share.card_desc": "Une vue en lecture seule que vos collègues peuvent ouvrir sans compte.",
    "share.enable_link": "Activer le lien",
    "share.copy_link": "Copier le lien",
    "share.disabled": "Partage désactivé",
    "gallery.subtitle": "Galerie publique",
    "gallery.kicker": "Découvrez de vraies classes",
    "gallery.title": "Voyez comment d'autres enseignants préparent leur année.",
    "gallery.desc": "De vrais programmes partagés par de vrais enseignants en France. Inspirez-vous et construisez le vôtre.",
    "gallery.empty_title": "Aucun programme public pour l'instant.",
    "gallery.empty_desc": "Soyez le premier ! Activez le partage depuis votre tableau de bord.",
    "gallery.card_default_title": "Programme d'anglais",
    "gallery.view": "Voir le programme",
    "gallery.link": "Galerie publique",
    "stats.kicker": "Votre progression",
    "stats.title": "Vue d'ensemble de l'année",
    "stats.taught": "Semaines enseignées",
    "stats.packed": "Kits prêts",
    "stats.homework": "Avec devoirs",
    "stats.streak": "Semaines restantes",
    "hw.send_families": "Envoyer aux familles",
    "hw.send_no_recipients": "Ajoutez d'abord les emails des familles dans votre Profil.",
    "hw.send_email_subject": "Devoirs d'anglais — Semaine {n} : {title}",
    "hw.send_email_body_1": "Bonjour les familles,",
    "hw.send_email_body_2": "Veuillez trouver ci-joint les devoirs d'anglais de la semaine : {title} (environ {min} minutes).",
    "hw.send_email_body_3": "Le PDF a été téléchargé sur votre ordinateur — merci de le joindre à cet email avant l'envoi.",
    "hw.send_email_body_4": "Cordialement,",
    "profile.family_emails": "Emails des familles (un par ligne)",
    "profile.family_emails_hint": "Utilisé pour créer un email de devoirs en un clic.",
    "profile.gallery_optin": "Afficher dans la galerie publique",
    "hw.generated": "Devoirs générés",
    "hw.download_ok": "Devoirs téléchargés",
    "hw.answers_ok": "Corrigé téléchargé",
    "hw.failed": "Échec de la génération des devoirs",
    "hw.draft_opened": "Brouillon d'email ouvert pour {n} familles",
    "pack.generated": "Kit enseignant généré — téléchargez les PDF ci-dessous",
    "pack.failed": "Échec de la génération du kit",
    "pack.teacher_ok": "Plan enseignant téléchargé",
    "pack.student_ok": "Fiches élèves téléchargées",
    "pack.answers_ok": "Corrigé téléchargé",
    "pdf.downloaded": "PDF téléchargé",
    "lesson.saved": "Cours enregistré",
    "lesson.save_failed": "Échec de l'enregistrement",
    "lesson.swap_ok": "Activité remplacée",
    "lesson.swap_failed": "Échec du remplacement",
    "lesson.ai_ok": "L'IA a suggéré une nouvelle activité",
    "lesson.ai_failed": "Échec de la génération IA",
    "chat.fab": "Demander à l'IA",
    "chat.title": "Coach de leçon",
    "chat.intro_a": "Je suis votre coach TEFL pour",
    "chat.intro_b": "Demandez-moi de reformuler, adapter le niveau ou suggérer un warmer.",
    "chat.placeholder": "Posez une question sur ce cours…",
    "chat.error": "Échec du chat — veuillez réessayer.",
    "chat.model_footer": "Propulsé par Claude Sonnet 5",
    "chat.suggest_rephrase": "Reformule l'activité de Production en anglais plus simple.",
    "chat.suggest_level_down": "Donne-moi une version plus facile pour un groupe timide.",
    "chat.suggest_warmer": "Suggère un warmer de 5 minutes sur ce thème.",
    "chat.suggest_example": "Donne 3 phrases concrètes utilisant la grammaire du jour.",
    "news.card_kicker": "Newsletter du dimanche",
    "news.card_title": "Rédiger l'email aux familles",
    "news.card_desc": "Un email sympa en un clic résumant la semaine {n} : thème, vocabulaire et devoirs.",
    "news.card_btn": "Rédiger la newsletter",
    "news.no_recipients": "Ajoutez les emails des familles dans le Profil pour envoyer une newsletter.",
    "news.subject": "Cette semaine en anglais — Semaine {n} : {title}",
    "news.body_hello": "Bonjour les familles,",
    "news.body_intro": "Voici ce que votre enfant va découvrir en anglais cette semaine :",
    "news.body_theme": "Thème :",
    "news.body_grammar": "Point de grammaire :",
    "news.body_vocab": "Vocabulaire clé :",
    "news.body_homework": "Devoirs :",
    "news.body_homework_yes": "Une petite fiche ({min} min). Demandez à votre enfant de vous la montrer.",
    "news.body_homework_no": "Pas de devoirs cette semaine — juste de la curiosité !",
    "news.body_share": "Programme complet :",
    "news.body_close": "Belle semaine,",
    "news.draft_opened": "Brouillon de newsletter ouvert",
  },
};

const I18nCtx = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("teachkit_lang") || "en");
  useEffect(() => { localStorage.setItem("teachkit_lang", lang); document.documentElement.lang = lang; }, [lang]);
  const t = (key, vars = {}) => {
    let s = TRANSLATIONS[lang]?.[key] || TRANSLATIONS.en[key] || key;
    Object.entries(vars).forEach(([k, v]) => { s = s.replace(`{${k}}`, v); });
    return s;
  };
  return <I18nCtx.Provider value={{ lang, setLang, t }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
